import { ExclamationCircleFilled, InfoCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import React from "react";
import styled from "styled-components";

import { Tooltip } from "../../../../components";
import { COLORS } from "../../../../constants";

type UsageState = "normal" | "warning" | "full";

const getUsageState = (used: number, limit: number): UsageState => {
  if (limit <= 0) {
    return "normal";
  }
  const ratio = used / limit;
  if (ratio >= 1) {
    return "full";
  }
  if (ratio >= 0.8) {
    return "warning";
  }
  return "normal";
};

const STATE_COLOR: Record<UsageState, string> = {
  normal: COLORS.coeblue4,
  warning: COLORS.orange2,
  full: COLORS.red,
};

interface PlanLimitUsageBarProps {
  /** Dashboards currently counting against the plan. */
  used: number;
  /** The plan's dashboard limit. */
  limit: number;
  /**
   * When set, renders the richer "meter" used on the My Dashboards / Admin header: a state-colored
   * bar (blue < 80%, amber ≥ 80%) with a header label, or — at/over the limit — a red banner pill
   * with an info tooltip carrying the downgrade grace-period deadline. Omit for the compact red
   * "limit reached" bar shown in modals.
   */
  meter?: {
    /** Deadline of an active downgrade grace period (ISO string), or null/undefined. */
    gracePeriodEndsDt?: string | null;
    /** Gate the grace-period tooltip (e.g. plan admins only). Defaults to true. */
    showGraceBanner?: boolean;
  };
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const UsageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const UsageText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.black85};
`;

const HeaderLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.black85};
  white-space: nowrap;
`;

const CountText = styled.div`
  font-size: 12px;
  color: ${COLORS.black45};
  white-space: nowrap;
`;

const Track = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: ${COLORS.grayBg};
  overflow: hidden;
`;

const Fill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $color }) => $color};
  border-radius: 999px;
`;

// At/over the limit the meter collapses into a red banner pill (no track), per design.
const LimitBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: ${COLORS.redBg};
  border-radius: 8px;
  padding: 8px 16px;
`;

/**
 * Dashboard plan usage bar. Compact red "X of Y used on your plan" for the limit-reached modals by
 * default; pass `meter` for the three-state header meter (blue bar / amber bar / red limit banner)
 * on My Dashboards / Admin.
 */
export const PlanLimitUsageBar: React.FC<PlanLimitUsageBarProps> = ({ used, limit, meter }) => {
  if (!meter) {
    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 100;
    return (
      <Wrapper>
        <UsageRow>
          <ExclamationCircleFilled style={{ color: COLORS.red, fontSize: 16 }} />
          <UsageText>
            {used} of {limit} used on your plan
          </UsageText>
        </UsageRow>
        <Track>
          <Fill $pct={pct} $color={COLORS.red} />
        </Track>
      </Wrapper>
    );
  }

  const { gracePeriodEndsDt, showGraceBanner = true } = meter;
  const state = getUsageState(used, limit);
  const color = STATE_COLOR[state];
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 100;

  // At/over the limit: a red banner pill replaces the progress bar. The downgrade grace-period
  // deadline (when present) moves into an info tooltip.
  if (state === "full") {
    return (
      <LimitBanner>
        <UsageRow>
          <ExclamationCircleFilled style={{ color: COLORS.red, fontSize: 16 }} />
          <HeaderLabel>Active dashboards limit reached</HeaderLabel>
          {gracePeriodEndsDt && showGraceBanner && (
            <Tooltip
              title={`You have until ${moment(gracePeriodEndsDt).format(
                "MMM D, YYYY"
              )} to archive down to ${limit}. After that, we'll keep your ${limit} most recently viewed dashboards and archive the rest.`}
            >
              <InfoCircleOutlined style={{ color: COLORS.black45, fontSize: 14 }} />
            </Tooltip>
          )}
        </UsageRow>
        <CountText>
          {used} of {limit} used on your plan
        </CountText>
      </LimitBanner>
    );
  }

  return (
    <Wrapper>
      <Header>
        {state === "normal" ? (
          <HeaderLabel>Active dashboards</HeaderLabel>
        ) : (
          <UsageRow>
            <ExclamationCircleFilled style={{ color, fontSize: 16 }} />
            <HeaderLabel>Active dashboards limit reached 80%</HeaderLabel>
          </UsageRow>
        )}
        <CountText>
          {used} of {limit} included on your plan
        </CountText>
      </Header>
      <Track>
        <Fill $pct={pct} $color={color} />
      </Track>
    </Wrapper>
  );
};
